import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { BookOpen, Mail, Send } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { ApiError } from '#/lib/api'
import { submitSupport } from '#/lib/api-misc'
import { useHydratedSession } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/support')({ component: Support })

function Support() {
  const { data: session } = useHydratedSession()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const nameV = name || session?.user?.name || ''
  const emailV = email || session?.user?.email || ''
  const send = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await submitSupport({ name: nameV, email: emailV, message })
      setSent(true)
      setMessage('')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : m.common_error_generic())
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <AppHeader />
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-4 rounded-2xl border bg-card p-6 text-center">
            <h1 className="text-2xl font-bold">{m.support_title()}</h1>
            <p className="text-muted-foreground">{m.support_desc()}</p>
            <div className="flex justify-center gap-2">
              <Button asChild><a href="mailto:support@bystep.dev"><Mail className="size-4" /> {m.support_email()}</a></Button>
              <Button asChild variant="secondary"><Link to="/docs"><BookOpen className="size-4" /> {m.support_docs()}</Link></Button>
            </div>
          </div>
          <p className="text-center text-xs uppercase tracking-wide text-muted-foreground">{m.support_form_or()}</p>
          <form onSubmit={send} className="space-y-4 rounded-2xl border bg-card p-6" data-testid="support-form">
            <div className="space-y-1">
              <h2 className="font-semibold">{m.support_form_title()}</h2>
              <p className="text-sm text-muted-foreground">{m.support_form_desc()}</p>
            </div>
            {sent ? <p className="rounded-xl bg-status-done/10 p-3 text-sm text-status-done" data-testid="support-sent">{m.support_form_sent()}</p> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sp-name">{m.support_form_name()}</Label>
                <Input id="sp-name" value={nameV} onChange={(e) => setName(e.target.value)} required maxLength={80} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sp-email">{m.support_form_email()}</Label>
                <Input id="sp-email" type="email" value={emailV} onChange={(e) => setEmail(e.target.value)} required maxLength={200} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sp-msg">{m.support_form_message()}</Label>
              <Textarea id="sp-msg" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required minLength={5} maxLength={4000} />
            </div>
            <Button type="submit" className="w-full" disabled={busy || !nameV || !emailV || message.trim().length < 5}>
              <Send className="size-4" /> {m.support_form_send()}
            </Button>
          </form>
        </div>
      </main>
    </>
  )
}
